import Axios, {AxiosInstance, AxiosRequestConfig} from "axios";
import {normalizeMediaUrls} from "@/utils/mediaUrl";
import {toastController} from "@ionic/vue";

export type ApiErrorPayload = {
    status?: number;
    success?: false;
    error?: string | Array<{ field: string; message: string }>;
    message?: string;
    isPremium?: boolean;
    type?: string;
    feature?: string;
    reason?: string;
    limit?: number;
    remaining?: number;
    resetAt?: string | null;
    data?: unknown;
};

type SleepHandler = () => void | Promise<void>;
type PremiumRequiredHandler = (error: ApiErrorPayload) => void | Promise<void>;
type UnauthorizedHandler = (error: ApiErrorPayload) => void | Promise<void>;

let sleepHandler: SleepHandler | null = null;
let premiumHandler: PremiumRequiredHandler | null = null;
let unauthorizedHandler: UnauthorizedHandler | null = null;
let handlingUnauthorized = false;

export function registerSleepModeHandler(handler: SleepHandler): void {
    sleepHandler = handler;
}

export function registerPremiumRequiredHandler(handler: PremiumRequiredHandler): void {
    premiumHandler = handler;
}

export function registerUnauthorizedHandler(handler: UnauthorizedHandler): void {
    unauthorizedHandler = handler;
}

function isApiFailure(payload: any): boolean {
    return payload && typeof payload === 'object' && payload.success === false;
}

function unwrapData(payload: any): any {
    if (payload && typeof payload === 'object' && 'success' in payload && 'data' in payload) {
        return payload.data;
    }
    return payload;
}

async function presentQuotaToast(error: ApiErrorPayload): Promise<void> {
    const reset = error.resetAt ? ` Resets ${new Date(error.resetAt).toLocaleString()}.` : '';
    const toast = await toastController.create({
        message: `${error.message || 'Quota exceeded'}.${reset}`,
        duration: 4000,
        position: 'bottom',
        color: 'warning',
    });
    await toast.present();
}

export default class ApiClient {

    axios: AxiosInstance;

    public errorHandler = (response: ApiErrorPayload | undefined) => {
        return Promise.reject(response ?? {status: 0, message: 'API Client Error'});
    }

    public tokenProvider = function (): Promise<string | null> {
        return Promise.resolve(null);
    }

    constructor(headers: Record<string, unknown> = {}) {
        this.axios = Axios.create({
            headers: {
                ...headers,
                Accept: 'application/json',
                'Content-Type': 'application/json',
                pwa: '1',
                platform: 'web',
            },
        });
    }

    setBaseUrl(baseUrl: string): void {
        this.axios.defaults.baseURL = baseUrl;
    }

    setHeader(headerName: string, headerValue: string): void {
        this.axios.defaults.headers.common[headerName] = headerValue;
    }

    clearHeader(headerName: string): void {
        delete this.axios.defaults.headers.common[headerName];
    }

    get(url: string, parameters: Record<string, unknown> = {}, token = true): Promise<any> {
        return this._call('get', url, {params: parameters}, token);
    }

    post(url: string, data: any = {}, token = true): Promise<any> {
        return this._call('post', url, data, token);
    }

    put(url: string, data: any = {}, token = true): Promise<any> {
        return this._call('put', url, data, token);
    }

    patch(url: string, data: any = {}, token = true): Promise<any> {
        return this._call('patch', url, data, token);
    }

    delete(url: string, data: any = {}, token = true): Promise<any> {
        return this._call('delete', url, data, token);
    }

    async upload(url: string, data: FormData, onProgress?: (progress: number) => void, method: 'POST' | 'PUT' | 'PATCH' = 'POST'): Promise<any> {
        const token = await this.tokenProvider();
        const baseUrl = this.axios.defaults.baseURL ?? '';
        const requestUrl = `${baseUrl.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open(method, requestUrl, true);
            if (token) {
                const raw = String(token).replace(/^Bearer\s+/i, '').trim();
                if (raw) {
                    xhr.setRequestHeader('Authorization', `Bearer ${raw}`);
                }
            }
            xhr.setRequestHeader('Accept', 'application/json');
            xhr.setRequestHeader('pwa', '1');
            xhr.setRequestHeader('platform', 'web');

            xhr.upload.onprogress = (event) => {
                if (onProgress && event.lengthComputable) {
                    onProgress(Math.round((event.loaded / event.total) * 100));
                }
            };

            xhr.onload = async () => {
                let payload: any = null;
                try {
                    payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
                } catch {
                    payload = xhr.responseText;
                }

                try {
                    const result = await this.handlePayload(payload, xhr.status);
                    resolve(result);
                } catch (error) {
                    reject(error);
                }
            };

            xhr.onerror = () => {
                reject({status: xhr.status || 0, message: 'Upload failed.'});
            };

            xhr.send(data);
        });
    }

    async _call(method: string, url: string, data: any, requiresToken = true): Promise<any> {
        if (requiresToken) {
            const token = await this.tokenProvider();
            const raw = token ? String(token).replace(/^Bearer\s+/i, '').trim() : '';
            if (raw) {
                this.setHeader('Authorization', `Bearer ${raw}`);
            } else {
                this.clearHeader('Authorization');
            }
        } else {
            this.clearHeader('Authorization');
        }

        const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
        const config: AxiosRequestConfig = {};

        if (isFormData) {
            this.clearHeader('Content-Type');
        } else if (method !== 'get') {
            this.setHeader('Content-Type', 'application/json');
        }

        try {
            let response;
            if (method === 'get') {
                response = await this.axios.get(url, data);
            } else if (method === 'delete') {
                response = await this.axios.delete(url, {data});
            } else {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                response = await this.axios[method](url, data, config);
            }
            return await this.handlePayload(response.data, response.status);
        } catch (error: any) {
            if (error?.response) {
                return this.handlePayload(error.response.data, error.response.status);
            }
            if (error?.status || error?.message) {
                return this.errorHandler(error);
            }
            return this.errorHandler({status: 0, message: 'Network error'});
        }
    }

    private async handlePayload(payload: any, status: number): Promise<any> {
        const normalized = normalizeMediaUrls(payload);

        if (this.isSleepError(normalized)) {
            if (sleepHandler) {
                await sleepHandler();
            }
            return this.errorHandler({...this.asError(normalized), status: 401});
        }

        if (status === 401) {
            const error = {...this.asError(normalized), status: 401};
            if (unauthorizedHandler && !handlingUnauthorized) {
                handlingUnauthorized = true;
                try {
                    await unauthorizedHandler(error);
                } finally {
                    handlingUnauthorized = false;
                }
            }
            return this.errorHandler(error);
        }

        if (status === 403 || (isApiFailure(normalized) && normalized.reason)) {
            const error = {...this.asError(normalized), status: status || 403};
            if (error.reason === 'premium_required' && premiumHandler) {
                await premiumHandler(error);
            } else if (error.reason === 'quota_exceeded') {
                await presentQuotaToast(error);
            }
            return this.errorHandler(error);
        }

        if (status >= 400 || isApiFailure(normalized)) {
            return this.errorHandler({...this.asError(normalized), status: status || 400});
        }

        return unwrapData(normalized);
    }

    private isSleepError(payload: any): boolean {
        const message = String(payload?.message || payload?.error || '');
        return message.toLowerCase().includes('sleep mode');
    }

    private asError(payload: any): ApiErrorPayload {
        if (!payload || typeof payload !== 'object') {
            return {success: false, message: String(payload || 'Request failed'), error: String(payload || 'Request failed')};
        }
        return {
            success: false,
            status: payload.status,
            error: payload.error,
            message: typeof payload.message === 'string'
                ? payload.message
                : (typeof payload.error === 'string' ? payload.error : 'Request failed'),
            isPremium: payload.isPremium,
            type: payload.type,
            feature: payload.feature,
            reason: payload.reason,
            limit: payload.limit,
            remaining: payload.remaining,
            resetAt: payload.resetAt,
            data: payload.data,
        };
    }

    public registerErrorHandler(callback: any): void {
        this.errorHandler = callback;
    }

    public registerTokenProvider(callback: any): void {
        this.tokenProvider = callback;
    }

}
